/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { EMPTY } from 'rxjs';
import { PlanState } from 'src/app/services/plan-state.service';
import { UndoItem } from './UndoItem';
import { UndoTypes, UndoTitles } from './UndoTypes';
import { BaseUndoOperation } from './base-undo-operation';

import { Ticket } from '@touchplan/common';
import { metaizeTicket } from './undo-utils';
import { TicketMeta } from 'src/app/app-data/models/TicketMeta';
import { ICustomFieldTicketList } from 'src/app/app-data/models/CustomFieldTicketValue';
import deepcopy from 'deepcopy';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { ITicketAttachmentList } from '../../app-data/models/Attachment';

interface IClipboardPasteInvalidationCache {}
interface IClipboardPasteUndoItem extends UndoItem {
	data: TicketMeta[];
	invalidationCache: IClipboardPasteInvalidationCache;
}

export class ClipboardPasteOperation extends BaseUndoOperation<
	IClipboardPasteInvalidationCache,
	IClipboardPasteUndoItem
> {
	public readonly undoType = UndoTypes.ticketPaste;

	public title = UndoTitles.ticketPaste;

	constructor(private planState: PlanState, private featureFlagManager: FeatureFlagManager) {
		super();
	}

	public snapshotUndoState(
		newTickets: Ticket[],
		customFieldsData: ICustomFieldTicketList,
		ticketAttachmentsData: ITicketAttachmentList
	) {
		this.onPush(
			this.makeUndoItem(this.decorateTicketData(newTickets, customFieldsData, ticketAttachmentsData))
		);
	}

	public applyUndo(undoItem: IClipboardPasteUndoItem) {
		const pastedTickets = undoItem.data;

		if (pastedTickets && pastedTickets.length) {
			this.planState.actions.internalDeleteTicket(pastedTickets.map((meta) => meta.ticket));
		}
		// note: custom field & ticket attachments deletion are already handled in by ticketEventHandling() in plan-state.service triggered by internalDeleteTicket()
		return EMPTY;
	}

	public applyRedo(undoItem: IClipboardPasteUndoItem) {
		const pastedTickets = undoItem.data;

		if (pastedTickets && pastedTickets.length) {
			pastedTickets.forEach((meta) => {
				this.planState.actions.createTicketWithId(meta.ticket.$id, meta.ticket.prepareDataForSave());
				this.planState.customFieldsApi.createCustomFieldTicketValues(
					meta.ticket.$id,
					meta.customFieldTicketValues
				);

				if (this.featureFlagManager.getFlag(DynamicFlagTypes.FILE_ATTACH_ENABLED)) {
					this.planState.fileAttachCloneService.copyAttachment$(
						this.planState.projectId,
						this.planState.planId,
						meta.ticket.$id,
						meta.ticketAttachmentsData
					);
				}
			});
		}

		return EMPTY;
	}

	public calculateCombinedInvalidation() {
		return {};
	}

	private decorateTicketData(
		data: Ticket[],
		customFieldsData: ICustomFieldTicketList,
		ticketAttachmentsData: ITicketAttachmentList
	) {
		const arr = [];
		data.forEach((ticket) =>
			arr.push(
				metaizeTicket(
					ticket,
					null,
					deepcopy(customFieldsData[ticket.$id]),
					deepcopy(ticketAttachmentsData[ticket.$id])
				)
			)
		);
		return arr;
	}

	private makeUndoItem(data: TicketMeta[]): IClipboardPasteUndoItem {
		return {
			title: this.title,
			data,
			undoType: this.undoType,
			invalidationCache: {},
		};
	}
}
